/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {useCallback, useEffect, useRef} from 'react';
import {useGeocoding} from '@amblique/king-living/app/hooks/use-geocoding';
import {resolveBrowserGeoAddress} from '@amblique/king-living/app/utils/browser-geo-address';
import {setPreferredLocaleToCookie} from '@amblique/king-living/app/utils/utils';
import {getPathWithLocale} from '@salesforce/retail-react-app/app/utils/url';

const STORAGE_KEY = 'kl_ca_provincial_locale_checked';
const STORAGE_EXPIRY_DAYS = 7;
const QUEBEC_PROVINCE_CODE = 'QC';

function isSearchEngineBot() {
  if (typeof navigator === 'undefined' || !navigator.userAgent) {
    return false;
  }

  const userAgent = navigator.userAgent.toLowerCase();

  const botPatterns = [
    'googlebot',
    'google-inspectiontool',
    'storebot-google',
    'bingbot',
    'msnbot',
    'bingpreview',
    'duckduckbot',
    'baiduspider',
    'yandexbot',
    'slurp',
    'sogou',
    'facebookexternalhit',
    'twitterbot',
    'linkedinbot',
    'whatsapp',
    'telegrambot',
    'ahrefsbot',
    'semrushbot',
    'dotbot',
    'rogerbot',
    'screaming frog',
    'applebot',
    'slackbot',
    'discordbot',
    'pinterestbot',
    'redditbot'
  ];

  return botPatterns.some((pattern) => userAgent.includes(pattern));
}

function shouldSkipPathForProvincialLocale(pathname, sitePreferences) {
  const resetPasswordCallback = sitePreferences?.resetPasswordCallback || '/reset-password-callback';
  const passwordlessLoginCallback = sitePreferences?.passwordlessLoginCallback || '/passwordless-login-callback';
  const socialLoginCallback = sitePreferences?.socialLoginCallback || '/social-callback';

  const skipPaths = [
    '/callback',
    resetPasswordCallback,
    passwordlessLoginCallback,
    socialLoginCallback,
    '/mobify/',
    '/static/',
    '/assets/',
    '/_next/',
    '/favicon.ico',
    '/robots.txt',
    '/sitemap.xml',
    '/worker.js',
    '/checkout',
    '/login',
    '/registration',
    '/reset-password',
    '/passwordless-login-landing'
  ];

  if (skipPaths.some((prefix) => pathname.startsWith(prefix))) {
    return true;
  }

  if (pathname.match(/^\/[^/]+\/[^/]+\/oauth2\/jwks$/)) {
    return true;
  }

  return false;
}

function readCache() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);

    if (!stored) {
      return null;
    }

    const data = JSON.parse(stored);
    const expiryTime = new Date(data.timestamp);

    expiryTime.setDate(expiryTime.getDate() + STORAGE_EXPIRY_DAYS);

    if (new Date() > expiryTime) {
      localStorage.removeItem(STORAGE_KEY);

      return null;
    }

    return data;
  } catch {
    return null;
  }
}

function writeCache(outcome) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        timestamp: new Date().toISOString(),
        outcome,
      })
    );
  } catch {
    // ignore
  }
}

/**
 * KINDW-710 / FSD: first visit on kl-ca with no saved locale — Quebec → fr-CA, rest of Canada → en-CA.
 * Skipped when locale cookie or account preferredLocale exists.
 * Uses client Google Geolocation (considerIp) + server reverse geocode (same stack as region redirect).
 *
 * @param {Object} options
 * @param {boolean} options.enabled
 * @param {string} options.siteId
 * @param {Function} options.buildUrl
 * @param {{ pathname: string }} options.location
 * @param {Object} [options.sitePreferences]
 * @param {Function} [options.onComplete] — called once when done (success, skip, or error)
 */
export function useCanadaProvincialLocale({
  enabled,
  siteId,
  buildUrl,
  location,
  sitePreferences,
  onComplete,
}) {
  const {reverseGeocode} = useGeocoding();
  const onCompleteRef = useRef(onComplete);

  onCompleteRef.current = onComplete;

  const finish = useCallback(() => {
    onCompleteRef.current?.();
  }, []);

  // useGeocoding's reverseGeocode → geocode() toggles loading state → App re-renders → getTokenWhenReady
  // often yields new function identities → effect deps churn → infinite restart. Refs keep one stable run.
  const reverseGeocodeRef = useRef(reverseGeocode);
  const finishRef = useRef(finish);
  const buildUrlRef = useRef(buildUrl);
  const sitePreferencesRef = useRef(sitePreferences);

  reverseGeocodeRef.current = reverseGeocode;
  finishRef.current = finish;
  buildUrlRef.current = buildUrl;
  sitePreferencesRef.current = sitePreferences;

  useEffect(() => {
    if (!enabled || siteId !== 'kl-ca') {
      return;
    }

    let aborted = false;

    const safeFinish = () => {
      if (!aborted) {
        finishRef.current?.();
      }
    };

    (async () => {
      if (isSearchEngineBot()) {
        safeFinish();

        return;
      }

      if (shouldSkipPathForProvincialLocale(location.pathname, sitePreferencesRef.current)) {
        safeFinish();

        return;
      }

      const cached = readCache();

      if (cached && 'outcome' in cached) {
        if (cached.outcome === 'none') {
          safeFinish();

          return;
        }

        try {
          if (cached.outcome === 'fr-CA') {
            setPreferredLocaleToCookie(siteId, 'fr-CA');
            window.location.href = getPathWithLocale(
              'fr-CA',
              buildUrlRef.current,
              {disallowParams: ['refine']}
            );
          } else if (cached.outcome === 'en-CA') {
            setPreferredLocaleToCookie(siteId, 'en-CA');
          }
        } catch {
          // ignore
        } finally {
          safeFinish();
        }

        return;
      }

      const applyCanadaFromRegion = (regionCode) => {
        if (aborted) {
          return;
        }

        const normalizedRegion = (regionCode || '').toUpperCase();
        const isQuebec = normalizedRegion === QUEBEC_PROVINCE_CODE;
        const targetLocale = isQuebec ? 'fr-CA' : 'en-CA';

        writeCache(targetLocale);
        setPreferredLocaleToCookie(siteId, targetLocale);

        if (targetLocale === 'fr-CA') {
          window.location.href = getPathWithLocale(
            'fr-CA',
            buildUrlRef.current,
            {disallowParams: ['refine']}
          );
        }

        safeFinish();
      };

      const apiKey = sitePreferencesRef.current?.GOOGLE_GEOLOCATION_API_KEY;

      if (!apiKey) {
        safeFinish();

        return;
      }

      try {
        const {countryCode, regionCode} = await resolveBrowserGeoAddress({
          siteId,
          apiKey,
          reverseGeocode: reverseGeocodeRef.current,
        });

        if (aborted) {
          return;
        }

        if (countryCode !== 'CA') {
          writeCache('none');
          safeFinish();

          return;
        }

        applyCanadaFromRegion(regionCode || '');
      } catch {
        safeFinish();
      }
    })();

    return () => {
      aborted = true;
    };
  }, [enabled, siteId, location.pathname]);
}
